import React, { useEffect, useState } from "react";
import { CompanyAdminLayout } from "../../../components/CompanyAdmin/CompanyAdminLayout";
import TextInput from "../../../components/UI/TextInput";
import Table from "../../../components/UI/Table";
import { Button } from "../../../components/UI/Button";
import { LucidePlus, ChevronUp, ChevronDown, Edit, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Modal from "../../../components/UI/Modal";
import Switch from "../../../components/UI/Switch";
import { useAuthStore } from "../../../stores/auth-store";
import { contentUseCase } from "../../../../di/container";
import type {
  Portfolio,
  PortfolioLinkedOrder,
} from "../../../../domain/types/content";
import {
  buildSaveResultMessage,
  resolveErrorMessage,
  runSaveSteps,
} from "../../../../shared/utils/save-flow";
import {
  createButtonDraft,
  type PortfolioButtonDraft,
} from "./components/PortfolioButtonsEditor";
import { PortfolioOrderPickerModal } from "./components/PortfolioOrderPickerModal";
import {
  buildContentSaveSteps,
  EMPTY_FORM,
  findButtonProblem,
  PortfolioFormFields,
  type PortfolioForm,
} from "./portfolio-form";

const PortfolioManagement: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [loadedData, setLoadedData] = useState<Portfolio[]>([]);
  const [loading, setLoading] = useState(false);

  /*
    헤더 메뉴와 /shop/portfolio 페이지 자체를 켜고 끄는 스위치
    (shops.portfolio_enabled).

    메인페이지의 포트폴리오 블록은 이 값이 아니라 shop_sections 가 정한다.
    둘을 하나로 묶으면 "메뉴에는 두되 메인에는 안 띄우기"가 불가능해지므로
    일부러 나눠 둔 값이다 — 쇼핑몰 디자인 화면에서 따로 켜고 끈다.
  */
  const [isSectionEnabled, setIsSectionEnabled] = useState(false);

  // 수정 모달 상태
  const [editId, setEditId] = useState<number | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editForm, setEditForm] = useState<PortfolioForm>(EMPTY_FORM);
  const [editThumbnail, setEditThumbnail] = useState<File | null>(null);
  const [editThumbnailPreview, setEditThumbnailPreview] = useState<string | null>(null);
  // null 이면 기존 추가 이미지를 유지하고, 파일을 고르면 통째로 교체한다.
  const [editImages, setEditImages] = useState<File[] | null>(null);
  const [editExistingImageCount, setEditExistingImageCount] = useState(0);
  const [editButtons, setEditButtons] = useState<PortfolioButtonDraft[]>([]);
  const [editOrders, setEditOrders] = useState<PortfolioLinkedOrder[]>([]);

  /*
    버튼과 연결 주문을 상세에서 못 읽었을 때의 사유.

    이 값이 있으면 두 항목의 편집을 아예 열지 않고 저장 단계에서도 건너뛴다.
    두 API 가 전체 치환이라, 못 읽은 상태를 빈 목록으로 간주해 보내면
    서버에 멀쩡히 있던 버튼과 연결 주문이 지워진다.
  */
  const [editContentError, setEditContentError] = useState<string | null>(null);

  /*
    수정 모달의 주문 선택 모달. 추가는 별도 페이지(PortfolioCreatePage)에서 한다.
  */
  const [isOrderPickerOpen, setIsOrderPickerOpen] = useState(false);

  const [isSaving, setIsSaving] = useState(false);

  const loadData = async () => {
    if (!user?.companyId) return;
    setLoading(true);
    try {
      const [portfolios, section] = await Promise.all([
        contentUseCase.getPortfolios(user.companyId),
        contentUseCase.getPortfolioSection(user.companyId),
      ]);
      setLoadedData(portfolios);
      setIsSectionEnabled(section.isEnabled);
    } catch (error) {
      alert(
        resolveErrorMessage(error, "포트폴리오 목록을 불러오는데 실패했습니다.")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.companyId) {
      loadData();
    }
  }, [user?.companyId]);

  const resetEditModal = () => {
    setEditId(null);
    setEditForm(EMPTY_FORM);
    setEditThumbnail(null);
    setEditThumbnailPreview(null);
    setEditImages(null);
    setEditExistingImageCount(0);
    setEditButtons([]);
    setEditOrders([]);
    setEditContentError(null);
  };

  const handleToggleSection = async () => {
    if (!user?.companyId) return;
    try {
      const section = await contentUseCase.updatePortfolioSection(
        user.companyId,
        !isSectionEnabled
      );
      setIsSectionEnabled(section.isEnabled);
    } catch (error) {
      alert(
        resolveErrorMessage(
          error,
          "포트폴리오 메뉴 노출 설정 변경에 실패했습니다."
        )
      );
    }
  };

  const handleUpdate = async () => {
    if (!user?.companyId || editId === null || isSaving) return;
    if (editForm.title.trim() === "") {
      alert("제목을 입력해주세요.");
      return;
    }

    const buttonProblem = findButtonProblem(editButtons);
    if (buttonProblem) {
      alert(buttonProblem);
      return;
    }

    const companyId = user.companyId;
    const portfolioId = editId;
    setIsSaving(true);

    try {
      const result = await runSaveSteps([
        {
          label: "포트폴리오",
          run: async () => {
            await contentUseCase.updatePortfolio(
              companyId,
              portfolioId,
              editForm,
              editThumbnail || undefined,
              editImages ?? undefined
            );
          },
        },
        // 못 읽은 값을 빈 목록으로 덮어쓰지 않는다
        ...(editContentError
          ? []
          : buildContentSaveSteps(
              companyId,
              portfolioId,
              editButtons,
              editOrders
            )),
      ]);

      await loadData();
      setIsEditModalVisible(false);
      resetEditModal();

      if (result.failed) {
        alert(buildSaveResultMessage(result));
      }
    } catch (error) {
      alert(resolveErrorMessage(error, "포트폴리오 수정에 실패했습니다."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleTogglePublished = async (portfolio: Portfolio) => {
    if (!user?.companyId) return;
    try {
      await contentUseCase.updatePortfolio(user.companyId, portfolio.id, {
        title: portfolio.title,
        summary: portfolio.summary ?? "",
        description: portfolio.description ?? "",
        clientName: portfolio.clientName ?? "",
        workedAt: portfolio.workedAt ?? "",
        isPublished: !portfolio.isPublished,
      });
      await loadData();
    } catch (error) {
      alert(resolveErrorMessage(error, "노출 여부 변경에 실패했습니다."));
    }
  };

  /**
   * 수정 모달 열기.
   *
   * 버튼과 연결 주문은 목록 응답에 없을 수 있어(상세에서만 내려주는 필드다)
   * 상세를 한 번 더 읽는다. 상세 조회가 실패하면 그 둘은 편집도 저장도 하지
   * 않는다 — 두 API 가 전체 치환이라 못 읽은 상태를 빈 목록으로 보내면
   * 서버에 있던 값이 지워진다.
   */
  const handleOpenEditModal = async (portfolio: Portfolio) => {
    if (!user?.companyId) return;

    setEditId(portfolio.id);
    setEditForm({
      title: portfolio.title,
      summary: portfolio.summary ?? "",
      description: portfolio.description ?? "",
      clientName: portfolio.clientName ?? "",
      workedAt: portfolio.workedAt ?? "",
      isPublished: portfolio.isPublished,
    });
    setEditThumbnailPreview(portfolio.thumbnailUrl);
    setEditThumbnail(null);
    setEditImages(null);
    setEditExistingImageCount(portfolio.images?.length ?? 0);
    setEditButtons([]);
    setEditOrders([]);
    setEditContentError(null);
    setIsEditModalVisible(true);

    try {
      const detail = await contentUseCase.getPortfolio(
        user.companyId,
        portfolio.id
      );

      /*
        백엔드 배포가 MES 보다 늦으면 두 필드가 응답에 아예 없다.
        그때는 빈 목록으로 두고, 화면에서도 "아직 없음"으로 보인다.
      */
      setEditButtons(
        (detail.buttons ?? []).map((button) =>
          createButtonDraft({
            label: button.label,
            url: button.url,
            size: button.size,
          })
        )
      );
      setEditOrders(detail.linkedOrders ?? []);
    } catch (error) {
      console.error(error);
      /*
        모달을 닫지도, alert 로 막지도 않는다. 나머지 항목(제목·본문·이미지)은
        멀쩡히 수정할 수 있고, 백엔드 미배포 구간에서는 이 실패가 매번 난다.
        대신 두 항목의 편집을 닫아 못 읽은 값을 덮어쓸 길 자체를 없앤다.
      */
      setEditContentError(
        resolveErrorMessage(error, "버튼과 연결 주문을 불러오지 못했습니다.")
      );
    }
  };

  const handleDelete = async (portfolioId: number) => {
    if (!user?.companyId) return;
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    try {
      await contentUseCase.deletePortfolio(user.companyId, portfolioId);
      await loadData();
    } catch (error) {
      alert(resolveErrorMessage(error, "포트폴리오 삭제에 실패했습니다."));
    }
  };

  const handleMoveOrder = async (index: number, direction: "up" | "down") => {
    if (!user?.companyId) return;
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= loadedData.length) return;

    const newData = [...loadedData];
    [newData[index], newData[targetIndex]] = [newData[targetIndex], newData[index]];

    setLoadedData(newData);

    try {
      await contentUseCase.reorderPortfolios(user.companyId, {
        portfolioIds: newData.map((p) => p.id),
      });
    } catch (error) {
      alert(resolveErrorMessage(error, "순서 변경에 실패했습니다."));
      await loadData();
    }
  };

  return (
    <>
      {isOrderPickerOpen && user?.companyId ? (
        <PortfolioOrderPickerModal
          isModalVisible
          setIsModalVisible={() => setIsOrderPickerOpen(false)}
          companyId={user.companyId}
          selectedOrders={editOrders}
          onChange={setEditOrders}
        />
      ) : null}

      {isEditModalVisible && (
        <Modal setIsModalVisible={setIsEditModalVisible} title="포트폴리오 수정">
          <>
            <div className="w-[calc(100vw-20vw)] max-w-[31.875rem] mb-4" />

            <div className="max-h-[60vh] overflow-y-auto pr-1">
              <PortfolioFormFields
                {...{
                form: editForm,
                setForm: setEditForm,
                thumbnailPreview: editThumbnailPreview,
                onThumbnailChange: (file) => {
                  setEditThumbnailPreview(URL.createObjectURL(file));
                  setEditThumbnail(file);
                },
                selectedImageCount: editImages?.length ?? 0,
                onImagesChange: (files) => setEditImages(files),
                imagesHint:
                  editExistingImageCount > 0
                    ? `현재 ${editExistingImageCount}장 등록됨 — 새로 고르지 않으면 그대로 유지됩니다.`
                    : "등록된 추가 이미지가 없습니다.",
                buttons: editButtons,
                onButtonsChange: setEditButtons,
                orders: editOrders,
                onOrdersChange: setEditOrders,
                onOpenOrderPicker: () => setIsOrderPickerOpen(true),
                contentError: editContentError,
                }}
              />
            </div>

            <Button
              onClick={handleUpdate}
              loading={isSaving}
              disabled={isSaving}
              className="w-full h-[45px] mt-2"
            >
              제출
            </Button>
          </>
        </Modal>
      )}

      <CompanyAdminLayout>
        <div className="p-5">
          <div className="mb-[28px] / flex flex-row justify-between gap-[0.5rem]">
            <h2 className="text-[1.375rem] / font-semibold">포트폴리오 관리</h2>
            <Button onClick={() => navigate("new")}>
              <LucidePlus className="scale-[0.9]" />
              추가하기
            </Button>
          </div>

          {/* 메뉴·페이지 노출 스위치. 메인페이지 블록은 쇼핑몰 디자인 화면에 있다 */}
          <div
            className="mb-[1.25rem] / p-[1.25rem]
          / border border-[#d9d9d9] / rounded-[0.625rem] / bg-white
          / flex flex-row items-center justify-between gap-[0.75rem]"
          >
            <div>
              <p className="text-[14px] font-medium text-[#222]">
                쇼핑몰 메뉴 · 포트폴리오 페이지 노출
              </p>
              <p className="mt-1 text-[13px] text-[#888]">
                끄면 쇼핑몰 메뉴에서 포트폴리오가 사라지고 주소로 직접 접근해도 열리지
                않습니다. 메인페이지의 포트폴리오 블록은 쇼핑몰 디자인 화면에서 따로
                켜고 끕니다.
              </p>
            </div>
            <div onClick={handleToggleSection} className="cursor-pointer shrink-0">
              <Switch isEnabled={isSectionEnabled} />
            </div>
          </div>

          <div
            className="mb-[1.25rem] / p-[1.25rem]
          / border border-[#d9d9d9] / rounded-[0.625rem] / bg-white
          / flex flex-col md:flex-row gap-[0.75rem] / justify-stretch"
          >
            <TextInput
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="w-full"
              placeholder="제목 또는 발주처로 검색"
            />
          </div>

          <div className="text-[12px]">
            <Table
              tabledata={{
                head: [
                  <>순서</>,
                  <>대표 이미지</>,
                  <>제목</>,
                  <>발주처</>,
                  <>진행 시기</>,
                  <>쇼핑몰 노출</>,
                  <></>,
                ],
                body: loadedData
                  .filter((item) => {
                    if (searchKeyword === "") return true;
                    const keyword = searchKeyword.toLowerCase();
                    return (
                      item.title.toLowerCase().includes(keyword) ||
                      (item.clientName ?? "").toLowerCase().includes(keyword)
                    );
                  })
                  .map((item, index) => [
                    <div className="flex items-center gap-1">
                      <button
                        className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30"
                        disabled={index === 0}
                        onClick={() => handleMoveOrder(index, "up")}
                      >
                        <ChevronUp size={16} />
                      </button>
                      <button
                        className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30"
                        disabled={index === loadedData.length - 1}
                        onClick={() => handleMoveOrder(index, "down")}
                      >
                        <ChevronDown size={16} />
                      </button>
                    </div>,
                    <img
                      src={item.thumbnailUrl || ""}
                      alt="thumb"
                      className="w-12 h-12 object-cover rounded"
                    />,
                    <>{item.title}</>,
                    <>{item.clientName || "-"}</>,
                    <>{item.workedAt || "-"}</>,
                    <div
                      onClick={() => handleTogglePublished(item)}
                      className="cursor-pointer"
                    >
                      <Switch isEnabled={item.isPublished} />
                    </div>,
                    <div className="flex flex-row gap-[6px]">
                      <Button
                        variant="outline"
                        onClick={() => handleOpenEditModal(item)}
                      >
                        <Edit className="w-4 h-4" />
                        수정
                      </Button>
                      <Button variant="outline" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="w-4 h-4" />
                        삭제
                      </Button>
                    </div>,
                  ]),
              }}
            />
          </div>
        </div>
      </CompanyAdminLayout>
    </>
  );
};

export default PortfolioManagement;
